// eslint-disable-next-line import/no-extraneous-dependencies
import { ACTIVE_OFFERS_SKUS } from 'components/content/constants';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { Buffer } from 'buffer';

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
});
export const formatCurrency = currencyFormatter.format;
export const isNumber = (value) => typeof value === 'number';
export const isString = (value) => typeof value === 'string';
export const isFunction = (value) => typeof value === 'function';
export const isObject = (value) => typeof value === 'object' && value !== null;
export const pickBy = (object) => {
  const obj = {};
  for (const key in object) {
    if (object[key]) {
      obj[key] = object[key];
    }
  }
  return obj;
};

export const goToCustomUrl = (url) => {
  window.location.href = url;
};

export const getUrlOffersParams = (location) => {
  let offerSKU = null;
  let offerId = null;
  let loc = null;

  const decodedParams = Buffer.from(location.search, 'base64').toString() || '';
  const hasCorrectParams = decodedParams.includes('offer') && decodedParams.includes('idx');
  const hasLocParams = decodedParams.includes('loc');
  const splitParams = decodedParams?.split('&');

  if (hasCorrectParams) {
    // check if has valid offer params
    const offer = splitParams.filter((el) => el.includes('offer='))[0];
    const idx = splitParams.filter((el) => el.includes('idx='))[0];
    // parse them appropriately
    offerSKU = typeof offer === 'string'
      ? offer.replace('offer=', '')
      : null;
    offerId = typeof idx === 'string'
      ? parseInt(idx.replace('idx=', ''), 10)
      : null;
  }

  // check if localizations are present
  if (hasLocParams) {
    const locParam = splitParams.filter((el) => el.includes('loc='))[0];
    loc = typeof locParam === 'string'
      ? locParam.replace('loc=', '')
      : null;
  }

  return { offerSKU, offerId, loc };
};

/**
 * Roland Cloud Manager sends its own LangPack codes (US, MX, JP, CZ...) rather than the
 * ISO 639-1 codes the site's copy is keyed by, so `?loc=mx` has to resolve to `es`. Only
 * the seven that actually differ are listed here; DE, FI, FR, HU, IT, NL, NO, PL, RO and
 * SK are spelled the same in both schemes and pass straight through.
 *
 * `br` and `se` are themselves ISO 639-1 codes (Breton, Northern Sami), but the site
 * carries no copy for either language, so reading them the way RCM means them is safe.
 */
const RCM_LOCALE_ALIASES = {
  us: 'en',
  mx: 'es',
  jp: 'ja',
  cz: 'cs',
  dk: 'da',
  br: 'pt',
  se: 'sv',
};

export const getLocalizationParam = (search, userLanguage) => {
  const urlParams = new URLSearchParams(search);
  const locParam = urlParams.get('loc');

  if (locParam) {
    // The alias table is applied only to an explicit ?loc=, because that is the one RCM
    // controls. Lower-cased too: the copy is keyed in lower case while RCM documents its
    // codes in upper case, so without this `?loc=JP` would fall back to English.
    const normalized = locParam.toLowerCase();
    return RCM_LOCALE_ALIASES[normalized] || normalized;
  }

  // Account and browser language are left exactly as they were. They carry real BCP-47
  // tags, where `br` and `se` mean Breton and Northern Sami rather than RCM's Portuguese
  // and Swedish, so the alias table must not reach them.
  return userLanguage || navigator.language.substring(0, 2);
};

export const getLocalizationCopyContent = (loc, content) => {
  // check if there's content for that localization
  if (content && content[loc]) {
    return content[loc];
  }
  // else return the default content
  return content.en;
};

export const getScreenSize = (width) => {
  if (width < 320) {
    return 'xs';
  }
  if (width >= 320 && width < 720) {
    return 'sm';
  }
  if (width >= 720 && width < 1024) {
    return 'md';
  }
  if (width >= 1024 && width < 1440) {
    return 'lg';
  }
  if (width >= 1440) {
    return 'xl';
  }
};

export const getCTABtnOffer = (ActiveCTAS, sku) => {
  return ActiveCTAS.filter((el) => el.productIds === sku)[0];
};

export const goToLogin = (sku, offerIdx, loginAndRedirectUrl, locParam) => {
  const encodedParams = Buffer.from(`offer=${sku}&idx=${offerIdx}${locParam ? `&${locParam}` : ''}`).toString('base64');
  // fer: don't forget to add as slash before the encoded params
  // otherwise the params will be lost
  const baseUrl = loginAndRedirectUrl?.includes('?')
    ? loginAndRedirectUrl
    : loginAndRedirectUrl?.replace(/\/*$/, '/');
  const redirectTo = `${baseUrl}?${encodedParams}`;
  window.location.href = redirectTo;
};

export const getOffers = (offers) => {
  const activeOffers = Object.values(offers) // loop over get customers-offers res object
    .map((plan) => plan // loop offers
      .filter(
        // filter offers that include the values from ACTIVE_OFFERS_SKUS in productIds
        ({ productIds }) => ACTIVE_OFFERS_SKUS.some((el) => productIds.includes(el)),
      ))
    .filter((plan) => plan.length > 0); // remove empty arrays

  const hasfoundOffers = activeOffers?.length ? activeOffers[0] : [];

  return hasfoundOffers; // remove parent array
};

dayjs.extend(duration);
dayjs.extend(isSameOrAfter);

export const getRemainingTime = (dateNextCharge) => {
  const now = dayjs();
  const chargeDate = dayjs(dateNextCharge);

  if (!chargeDate.isAfter(now)) return '0 minutes';

  const diffMs = chargeDate.diff(now);
  const diffDuration = dayjs.duration(diffMs);

  const days = Math.floor(diffDuration.asDays());
  if (days > 0) return `${days} day${days === 1 ? '' : 's'}`;

  const hours = diffDuration.hours();
  if (hours > 0) return `${hours} hour${hours === 1 ? '' : 's'}`;

  const minutes = diffDuration.minutes();
  if (minutes > 0) return `${minutes} minute${minutes === 1 ? '' : 's'}`;

  return '0 minutes';
};
